import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { User } from "lucide-react";
import { useCreateLora, useLoraBaseModels } from "../../hooks/loras";

interface CastCreateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (loraId: string) => void;
}

export const CastCreateDialog: React.FC<CastCreateDialogProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [baseModelSlug, setBaseModelSlug] = useState("flux-dev");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const navigate = useNavigate();
  const createLora = useCreateLora();
  const { data: baseModels } = useLoraBaseModels();

  // Only image-output base models are usable for the v1 (image) wizard.
  const availableBaseModels = (baseModels ?? []).filter(
    (m) => m.isAvailable !== false && m.outputMediaType?.startsWith("image/"),
  );

  // Reset form when dialog opens. Default to the first available base model
  // (falls back to flux-dev) so the choice is always valid.
  useEffect(() => {
    if (isOpen) {
      setName("");
      setDescription("");
      setBaseModelSlug(availableBaseModels[0]?.slug ?? "flux-dev");
      setSubmitError(null);
    }
    // availableBaseModels is derived from a cached query; intentionally not a dep
    // (we only want to seed the default on open).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleCreate = () => {
    if (!name.trim() || createLora.isPending) return;
    setSubmitError(null);
    createLora.mutate(
      {
        name: name.trim(),
        kindSlug: "subject", // Cast = characters = subject LoRAs
        baseModelSlug,
        visibilitySlug: "user_private",
        description: description.trim() || null,
      },
      {
        onSuccess: (lora) => {
          onClose();
          onSuccess?.(lora.id);
          navigate(`?cast=${lora.id}&tab=train`);
        },
        onError: (err) => {
          setSubmitError(
            err instanceof Error ? err.message : "Failed to create LoRA",
          );
        },
      },
    );
  };

  const handleClose = () => {
    if (createLora.isPending) return;
    setName("");
    setDescription("");
    setSubmitError(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-[425px] p-0 gap-0" hideCloseButton>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleCreate();
          }}
        >
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle className="flex items-center gap-2">
              <User
                className="h-5 w-5"
                style={{ color: "var(--color-primitive-pink-500)" }}
              />
              Create New Character
            </DialogTitle>
            <DialogDescription>
              Add a new character to your project
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 px-6 py-5">
            {/* Base Model Selection — only shown when more than one is available */}
            {availableBaseModels.length > 1 && (
              <div className="grid gap-2">
                <label
                  className="text-sm font-medium"
                  style={{
                    color: "rgba(var(--color-primitive-neutral-0-rgb), 0.9)",
                  }}
                >
                  Base model *
                </label>
                <div
                  className="grid grid-cols-2 gap-2"
                  role="radiogroup"
                  aria-label="Base model"
                >
                  {availableBaseModels.map((model) => {
                    const isSelected = baseModelSlug === model.slug;
                    return (
                      <button
                        key={model.slug}
                        type="button"
                        role="radio"
                        aria-checked={isSelected}
                        onClick={() => setBaseModelSlug(model.slug)}
                        className="flex items-center justify-center px-3 py-2 text-[12px] transition-all"
                        style={{
                          borderRadius: "var(--radius-default)",
                          background: isSelected
                            ? "rgba(var(--color-primitive-pink-500-rgb), 0.12)"
                            : "rgba(var(--color-primitive-neutral-0-rgb), 0.05)",
                          border: isSelected
                            ? "1px solid var(--color-primitive-pink-500)"
                            : "1px solid rgba(var(--color-primitive-neutral-0-rgb), 0.12)",
                          color: isSelected
                            ? "var(--color-primitive-neutral-0)"
                            : "rgba(var(--color-primitive-neutral-0-rgb), 0.7)",
                          fontWeight: isSelected ? 500 : 400,
                          cursor: "pointer",
                        }}
                      >
                        {model.displayName}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Name Input */}
            <div className="grid gap-2">
              <label
                htmlFor="cast-name"
                className="text-sm font-medium"
                style={{
                  color: "rgba(var(--color-primitive-neutral-0-rgb), 0.9)",
                }}
              >
                Name *
              </label>
              <Input
                id="cast-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter character name"
                autoFocus
                className="text-sm"
                style={{
                  height: "36px",
                  borderRadius: "var(--radius-default)",
                  backgroundColor: "var(--color-background)",
                }}
              />
            </div>

            {/* Description Input */}
            <div className="grid gap-2">
              <label
                htmlFor="cast-description"
                className="text-sm font-medium"
                style={{
                  color: "rgba(var(--color-primitive-neutral-0-rgb), 0.9)",
                }}
              >
                Description
              </label>
              <Input
                id="cast-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description (optional)"
                className="text-sm"
                style={{
                  height: "36px",
                  borderRadius: "var(--radius-default)",
                  backgroundColor: "var(--color-background)",
                }}
              />
            </div>

            {submitError && (
              <div
                className="text-sm"
                role="alert"
                style={{
                  color: "var(--color-primitive-pink-500)",
                  background: "rgba(var(--color-primitive-pink-500-rgb), 0.08)",
                  border:
                    "1px solid rgba(var(--color-primitive-pink-500-rgb), 0.3)",
                  padding: "8px 12px",
                  borderRadius: "var(--radius-default)",
                }}
              >
                {submitError}
              </div>
            )}
          </div>

          <DialogFooter className="px-6 py-4 border-t gap-2">
            {/* Cancel - utility button */}
            <Button
              type="button"
              variant="utility"
              size="sm"
              onClick={handleClose}
            >
              Cancel
            </Button>

            {/* Create Character - solid pink/primary */}
            <Button
              type="submit"
              variant="pink"
              size="sm"
              disabled={!name.trim() || createLora.isPending}
            >
              {createLora.isPending ? "Creating…" : "Create Character"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CastCreateDialog;
